import { getAppInfo, getDeviceInfo, isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useEffect, useState } from "react";
import semver from "semver";

import { USER_EVENTS } from "../../constants/userEvents";
import { getMobileAppVersionsPostLogin } from "../appVersion/api";
import { VersionInfoResponse } from "../appVersion/model";

export const Version = (): JSX.Element | null => {
  const [appVersion, setAppVersion] = useState("");
  const [isVersionFetched, setIsVersionFetched] = useState(false);
  const platformIsCapacitor = isCapacitorPlatform();
  const [otaBuildId, setOtaBuildId] = useState<string | undefined>(undefined);

  function checkForLatestVersionAndLogEvent(
    installedAppVersion: string,
    latestMobileVersion: string
  ) {
    try {
      // If current app version is not set
      // that means a problem, and we should ask user to update app
      if (!installedAppVersion || !semver.valid(installedAppVersion)) {
        logEvent(USER_EVENTS.APP_INVALID_VERSION, {
          installedAppVersion,
          latestMobileVersion,
        });
        return false;
      }
      if (latestMobileVersion?.length && semver.valid(latestMobileVersion)) {
        return !semver.gt(latestMobileVersion, installedAppVersion);
      } else {
        // Backend issue: it returns not semver version
        // Valid semver sample: 1.2.3
        // Invalid semver sample: 1.2
        logEvent(USER_EVENTS.APP_INVALID_VERSION, {
          installedAppVersion,
          latestMobileVersion,
        });
        return true;
      }
    } catch {
      logEvent(USER_EVENTS.APP_INVALID_VERSION, {
        installedAppVersion,
        latestMobileVersion,
      });
      return true;
    }
  }

  useEffect(() => {
    (async () => {
      let installedAppVersion = "";
      let installedOtaBuildId: string | undefined;
      let lastAvailVersion: VersionInfoResponse | undefined;
      if (platformIsCapacitor) {
        ({ version: installedAppVersion, otaBuildId: installedOtaBuildId } = await getAppInfo());
        setAppVersion(installedAppVersion);
        setOtaBuildId(installedOtaBuildId);
      }

      if (installedAppVersion) {
        const { platform } = await getDeviceInfo();
        lastAvailVersion = await getMobileAppVersionsPostLogin(installedAppVersion, platform);
      }

      checkForLatestVersionAndLogEvent(
        installedAppVersion,
        lastAvailVersion?.latestMobileVersion?.mobileVersion ?? ""
      );
      setIsVersionFetched(true);
    })();
  }, [platformIsCapacitor]);

  return platformIsCapacitor && isVersionFetched ? (
    <div
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        marginTop: 24,
        marginBottom: 80,
        flexDirection: "column",
      }}
    >
      <span style={{ color: "#BDBDBD" }} data-testid="version">
        version {appVersion}
      </span>
      {otaBuildId && (
        <span style={{ color: "#BDBDBD" }} data-testid="version">
          build id {otaBuildId}
        </span>
      )}
    </div>
  ) : null;
};
